import { DatePipe, NgStyle } from '@angular/common';
import {
    Component,
    computed,
    DestroyRef,
    Injector,
    inject,
    input,
    type OnDestroy,
    type OnInit,
    type Signal,
    signal,
    viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService, type MenuItem } from '@openng/optimus-ui/api';
import { AvatarModule } from '@openng/optimus-ui/avatar';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { MenuModule } from '@openng/optimus-ui/menu';
import { MessageModule } from '@openng/optimus-ui/message';
import { SelectButtonModule } from '@openng/optimus-ui/selectbutton';
import { TagModule } from '@openng/optimus-ui/tag';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { DxDataGridComponent, DxDataGridModule } from 'devextreme-angular/ui/data-grid';
import { orderBy } from 'lodash-es';
import moment from 'moment';
import { injectParams } from 'ngxtension/inject-params';
import { forkJoin, map, type Observable, type Subscription, tap, timer } from 'rxjs';

import { AuthenticationService } from '@app/auth/services/authentication.service';
import type { FoundationModelService } from '@app/modules/assets/services/foundation-model.service';
import type { GlobalFunctionService } from '@app/modules/assets/services/global-function.service';
import type { PipelineService } from '@app/modules/assets/services/pipeline-builder.service';
import type { PromptService } from '@app/modules/assets/services/prompt.service';
import type { RagService } from '@app/modules/assets/services/rag.service';
import type { ReportsService } from '@app/modules/assets/services/reports.service';
import { SimulationService } from '@app/modules/testing/services/simulation.service';
import { DialogBoxComponent } from '@app/shared/components/dialog-box/dialog-box.component';
import { ExecutionOptionsComponent } from '@app/shared/components/execution-options/execution-options.component';
import { InputContainerComponent } from '@app/shared/components/input-container/input-container.component';
import { JobRuntimeInfoComponent } from '@app/shared/components/run-job/run-job-details/job-runtime-info/job-runtime-info.component';
import type { RunJobQueryParams } from '@app/shared/components/run-job/run-job-details/run-job-details.component';
import { SkeletonComponent } from '@app/shared/components/skeleton/skeleton.component';
import { StatusIconComponent } from '@app/shared/components/status-icon/status-icon.component';
import {
    ACTIONS,
    EXECUTION_TYPE,
    MOMENT_UTC_DATETIME_FORMAT,
    OBJECTS,
    type PAGES,
    SCHEDULED_JOB_STATUS,
    SIMULATABLE_OBJECTS,
    SKELETON_TYPES,
    WORKFLOWABLE_OBJECTS,
} from '@app/shared/constants/shared.constant';
import type { DxDataGrid } from '@app/shared/interface/devextreme.interface';
import type { NgActivatedRoute } from '@app/shared/interface/ng-router.interface';
import { type JobAdapter, JobModel } from '@app/shared/models/job.model';
import { LocalDatePipe } from '@app/shared/pipes/date-format.pipe';
import { DateFromNowPipe } from '@app/shared/pipes/date-from-now.pipe';
import { NameBadgePipe } from '@app/shared/pipes/name-badge.pipe';
import { UniqueRgbColorPipe } from '@app/shared/pipes/unique-rgb-color.pipe';
import { ApiErrorService } from '@app/shared/services/api-error.service';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { ClipboardService } from '@app/shared/services/clipboard.service';
import type { ListResponse } from '@app/shared/services/mixins/crud.service';
import { WithJobService } from '@app/shared/services/mixins/job.service';
import { WithWorkflowableService } from '@app/shared/services/mixins/workflow.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { Helpers, parseAsNumber } from '@app/shared/utils/helpers';
import { OBJECT_SERVICE_MAPPER } from '@app/shared/utils/object-service-mapper';
import { RouterHelper } from '@app/shared/utils/router-helper';

enum USER_FILTER {
    MY_JOBS = 'My Jobs',
    ALL_JOBS = 'All Jobs',
    VERIFIED_JOBS = 'Verified Jobs',
    RELATED_JOBS = 'Related Jobs',
}

@Component({
    selector: 'cor-job-history',
    templateUrl: './job-history.component.html',
    imports: [
        ReactiveFormsModule,
        DxDataGridModule,
        InputContainerComponent,
        JobRuntimeInfoComponent,
        StatusIconComponent,
        LocalDatePipe,
        DialogBoxComponent,
        SkeletonComponent,
        DatePickerModule,
        MenuModule,
        SelectButtonModule,
        MessageModule,
        TooltipModule,
        DatePipe,
        ExecutionOptionsComponent,
        DateFromNowPipe,
        UniqueRgbColorPipe,
        CardModule,
        NameBadgePipe,
        ButtonModule,
        NgStyle,
        TagModule,
        InputTextModule,
        TextareaModule,
        AvatarModule,
    ],
})
export class JobHistoryComponent implements OnInit, OnDestroy {
    readonly id = input(undefined, { transform: parseAsNumber });
    protected readonly currentProject = injectParams<string>('projectSlug');
    readonly dataGrid = viewChild(DxDataGridComponent);
    private readonly route = inject(ActivatedRoute) as NgActivatedRoute<{
        object: OBJECTS;
        id: number;
        page: PAGES;
    }>;

    protected readonly object = toSignal(this.route.data.pipe(map(data => data.object)));

    protected readonly OBJECTS = OBJECTS;

    protected readonly service: Signal<
        (
            | FoundationModelService
            | PromptService
            | RagService
            | PipelineService
            | ReportsService
            | GlobalFunctionService
        ) &
            WithJobService
    > = computed(() => this.injector.get<any>(OBJECT_SERVICE_MAPPER[this.object()]));

    readonly jobList = signal<(JobModel & { actionList?: MenuItem[] })[]>([]);

    protected pinnedJob = {
        list: signal<(JobModel & { actionList?: MenuItem[] })[]>([]),
        isLoading: signal(false),
    };

    protected SIMULATABLE_OBJECTS = SIMULATABLE_OBJECTS;
    protected USER_FILTER = USER_FILTER;

    protected editJob = {
        isEditModalOpen: signal(false),
        job: signal<JobModel>(null),
        editJobForm: null as FormGroup<{
            id: FormControl<number>;
            name: FormControl<string>;
            description: FormControl<string>;
            submissionDate: FormControl<Date>;
        }>,
        isSaving: signal(false),
    };

    protected readonly removeOldLabelDialog = {
        visible: signal(false),
        job: signal<JobModel>(null),
        commentControl: new FormControl<string>(''),
        onShow: (job: JobModel) => {
            this.removeOldLabelDialog.job.set(job);
            this.removeOldLabelDialog.commentControl.reset('');
            this.removeOldLabelDialog.visible.set(true);
        },
        submit: () => {
            const job = this.removeOldLabelDialog.job();
            if (!job) return;

            this.simulationService
                .removeOldLabel(job.id, {
                    comment: this.removeOldLabelDialog.commentControl.value,
                })
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(({ result }) => {
                    const updatedJob = JobModel.createFromAPI(result);
                    this.jobList.update(jobList =>
                        jobList.map(simulation =>
                            simulation.id === updatedJob.id
                                ? { ...updatedJob, actionList: this.getActionList(updatedJob) }
                                : simulation,
                        ),
                    );
                    this.removeOldLabelDialog.visible.set(false);
                    this.toast.showSuccess('“Old” label removed');
                });
        },
    };

    private jobSubscriber: Subscription;

    protected userFilterItems: { value: USER_FILTER; text: string }[];

    protected usersFilterFormControl = new FormControl<USER_FILTER>(null);

    private readonly authService = inject(AuthenticationService);
    private readonly appConfigService = inject(AppConfigService);
    private readonly injector = inject(Injector);
    private readonly router = inject(Router);
    private readonly toast = inject(ToastNotificationService);
    private readonly confirmationService = inject(ConfirmationService);
    private readonly permissionService = inject(PermissionService);
    protected readonly clipboardService = inject(ClipboardService);
    protected readonly simulationService = inject(SimulationService);

    private readonly destroyRef = inject(DestroyRef);
    protected readonly apiErrorService = inject(ApiErrorService);

    private readonly jobPullInterval = computed(
        () => this.appConfigService.appConfig().jobPullInterval || 60000,
    );
    protected readonly pageSize = computed(
        () => this.appConfigService.appConfig().paginationItemsPerPage,
    );

    protected readonly isLoading = signal(false);

    protected readonly initJobLoded = signal(false); // Flag to check initial jobList loading is done or not

    protected readonly lastUpdatedTime = signal<Date>(null);

    protected readonly SKELETON_TYPES = SKELETON_TYPES;
    protected readonly EXECUTION_TYPE = EXECUTION_TYPE;

    ngOnInit() {
        const userFilters: USER_FILTER[] = [];
        if (SIMULATABLE_OBJECTS.includes(this.object())) {
            userFilters.push(USER_FILTER.MY_JOBS);
        }
        if (SIMULATABLE_OBJECTS.includes(this.object())) {
            // This component is used for cases like GlobalFunction which are workflowable - but not simulatable
            // In those cases, there is no "All jobs" as GlobalFunction has no jobs of its own.
            userFilters.push(USER_FILTER.ALL_JOBS);
        }
        if (WORKFLOWABLE_OBJECTS.includes(this.object())) {
            // Verified Jobs should only be shown if object is workflowable
            userFilters.push(USER_FILTER.VERIFIED_JOBS);
        }
        if (
            [
                // Resources that can be used in Simulation > Data Source
                OBJECTS.GLOBAL_FUNCTION,
                OBJECTS.FOUNDATION_MODEL,
                OBJECTS.PROMPT,
                OBJECTS.RAG,
                OBJECTS.PIPELINE,
                // Objects that are selected in Simulation > Dashboard
                OBJECTS.REPORT,
            ].includes(this.object())
        ) {
            // Related jobs are jobs where this object was used in the other item's Job (DataSource, Dashboard)
            userFilters.push(USER_FILTER.RELATED_JOBS);
        }

        this.userFilterItems = userFilters.map(value => {
            return { value, text: value };
        });
        this.usersFilterFormControl.patchValue(userFilters[0]);

        if (this.id()) {
            // In case jobInterval is not provided, fetch the job list once
            // This is also a solution to prevent the polling when adding unit test to avoid
            // stabilize issues
            this.getSimulations();
            if (this.jobPullInterval()) {
                this.startPollingInterval(this.getSimulations.bind(this));
            }
        }

        this.usersFilterFormControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.getSimulations();
            });
        this.getPinnedSimulations();
    }

    private getPinnedSimulations() {
        this.pinnedJob.isLoading.set(true);
        this.simulationService
            .getDataList({
                entityId: this.id(),
                simple: true,
                isPinned: true,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(({ result }) => {
                this.pinnedJob.list.set(
                    orderBy(
                        result.map(sim => {
                            const job = JobModel.createFromAPI(sim);

                            return { ...job, actionList: this.getActionList(job) };
                        }),
                        'createdDate',
                        ['desc'],
                    ),
                );
            })
            .add(() => this.pinnedJob.isLoading.set(false));
    }

    /**
     * Start/Stop simulation polling based runtime info dialog
     * visibility state.
     * @param visible runtime info pop up visibility state
     */
    protected toggleRuntimeInfo(visible: boolean) {
        if (visible) {
            // need to stop polling while the info is being shown
            // because dialog box is part of devex which refreshes when polling is done
            this.jobSubscriber?.unsubscribe();
        } else {
            this.startPollingInterval(this.getSimulations.bind(this));
        }
    }

    private startPollingInterval(callbackFunction: () => void): void {
        this.jobSubscriber?.unsubscribe();
        this.jobSubscriber = timer(this.jobPullInterval())
            .pipe(tap(callbackFunction), takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }

    private getActionList(
        simulation: ArrayItem<SignalValue<JobHistoryComponent['jobList']>>,
    ): MenuItem[] {
        if (simulation.entityId !== this.id()) {
            // Actions can only be done when the simulation is for the current object that we are seeing.
            // For other cases, the user needs to go to the object where it was run
            // FIXME: We are cheking for the ID, but not for the simulation.objectType - Need to check that too
            //        for an accurate check on the current object.
            return [];
        }

        const actionList: MenuItem[] = [
            { id: 're-run', label: 'Rerun', command: () => this.onReRun(simulation) },
        ];

        /** whether the user has write access of the object */
        const isWriteAccess = this.permissionService.canAccessObject(
            Helpers.getPermissionName(this.object(), ACTIONS.WRITE),
        );

        if (isWriteAccess) {
            actionList.push({
                id: 'pin-dashboard',
                label: simulation.isPinned ? 'Unpin Dashboard' : 'Pin Dashboard',
                command: () => this.pinDashboard(simulation),
            });
            if (simulation.isOld) {
                actionList.push({
                    id: 'remove-old-label',
                    label: 'Remove "old" label',
                    command: () => this.removeOldLabelDialog.onShow(simulation),
                });
            }
        }
        if (simulation.jobStatus === SCHEDULED_JOB_STATUS.COMPLETED) {
            actionList.push({
                id: 're-run-reports',
                label: 'Rerun Reports',
                tooltip:
                    'This will rerun the Reports - but reuse the job output from the existing job.',
                command: () => this.onReRunReports(simulation),
            });

            if (simulation.objectType === OBJECTS.PIPELINE) {
                actionList.push({
                    id: 'hill-climb-on-prompt',
                    label: 'Hillclimb on Prompt',
                    tooltip: 'Try changing prompts to find the optimal solution for your pipeline',
                    command: () => this.onHillClimbOnPrompt(simulation),
                });
            }
        }
        if (!simulation.isVerified) {
            if (simulation.createdBy === this.authService.currentUser()?.name) {
                actionList.push({
                    id: 'edit',
                    label: 'Edit',
                    command: () => this.onEditJob(simulation),
                });
            }

            if (isWriteAccess) {
                actionList.push({
                    id: 'delete',
                    label: 'Delete',
                    command: () => this.onDeleteJob(simulation),
                });
            }
        }

        return actionList;
    }

    /**
     * Method to fetch simulations
     *
     * Case 1: Object is Simulatable
     *    Sub-case 1: Object is Workflowable:
     *      APIs to be called when Dropdown selection is:
     *      1. ALL_JOBS - getDataList() + getVerifiedSimulations()
     *      2. MY_JOBS - getDataList()
     *      3. VERIFIED_JOBS - getVerifiedSimulations()
     *
     *    Sub-case 2: Object is Simulatable but not Workflowable (Eg. QUALITY_PROFILE):
     *      APIs to be called when Dropdown selection is:
     *      1. ALL_JOBS - getDataList()
     *
     *   Note: getEligibleMetricsMdInfo() API will be called in all scenarios.
     *   Order of apis: [getEligibleMetricsMdInfo(), getSimulationByQuery(), getVerifiedSimulations()]
     *
     * Case 2: Object is NOT Simulatable but Workflowable
     *   APIs to be called when Dropdown selection is:
     *   1. VERIFIED_JOBS - Only call verified-jobs API.
     */
    getSimulations(): void {
        this.isLoading.set(true);
        this.dataGrid()?.instance?.beginCustomLoading('');
        if (SIMULATABLE_OBJECTS.includes(this.object())) {
            this.getSimulatableObjectSimulations();
        } else {
            this.getNonSimulatableObjectSimulations();
        }
    }

    private getSimulatableObjectSimulations() {
        // NOTE: We are adding this check here for better typesafety. Before this function is called, we check
        //       this.object to ensure the object is simulatable
        if (!(this.service() instanceof WithJobService)) throw Error('Expected Job service');

        // Preparing the list of APIs to be called
        const ob$: Observable<ListResponse<JobAdapter>>[] = [];
        if (this.usersFilterFormControl.value === USER_FILTER.ALL_JOBS) {
            ob$.push(
                this.simulationService.getDataList({
                    entityId: this.id(),
                    objectType: this.object(),
                    simple: true,
                }),
            );
        } else if (this.usersFilterFormControl.value === USER_FILTER.MY_JOBS) {
            ob$.push(
                this.simulationService
                    .getDataList({
                        entityId: this.id(),
                        objectType: this.object(),
                        simple: true,
                    })
                    .pipe(
                        map(({ result }) => ({
                            result: result.filter(
                                sim => sim.createdBy === this.authService.currentUser().name,
                            ),
                        })),
                    ),
            );
        } else if (this.usersFilterFormControl.value === USER_FILTER.VERIFIED_JOBS) {
            if (!(this.service() instanceof WithWorkflowableService))
                throw Error('Expected Workflowable service');
            ob$.push(this.service().getVerifiedSimulations(this.id()));
        } else if (this.usersFilterFormControl.value === USER_FILTER.RELATED_JOBS) {
            if (!(this.service() instanceof WithWorkflowableService))
                throw Error('Expected Workflowable service');
            ob$.push(this.service().getRelatedSimulations(this.id()));
        }

        forkJoin(ob$)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(([...response]) => {
                const result = response.flatMap(x => x.result);
                // NOTE: If initial JobList is empty then show all jobs only for 1st time
                if (result.length === 0 && !this.initJobLoded()) {
                    this.dataGrid()?.instance?.beginCustomLoading('');
                    this.usersFilterFormControl.patchValue(USER_FILTER.ALL_JOBS);
                }

                this.jobList.set(
                    orderBy(
                        result.map(sim => {
                            const job = JobModel.createFromAPI(sim);

                            return { ...job, actionList: this.getActionList(job) };
                        }),
                        'createdDate',
                        ['desc'],
                    ),
                );
            })
            .add(() => {
                this.dataGrid()?.instance?.endCustomLoading();
                // NOTE: Keep the loading state if initial jobList is empty and we are going to show all jobs now
                if (this.jobList().length === 0 && !this.initJobLoded()) {
                    this.initJobLoded.set(true);
                } else {
                    this.isLoading.set(false);
                    this.lastUpdatedTime.set(new Date());
                }
            });
    }

    private getNonSimulatableObjectSimulations() {
        // NOTE: We are adding this check here for better typesafety. Before this function is called, we check
        //       this.object to ensure the object is workflowable but not simulatable
        if (!(this.service() instanceof WithWorkflowableService))
            throw Error(`Expected Workflowable service`);

        let ob$: Observable<ListResponse<JobAdapter>>;
        if (this.usersFilterFormControl.value === USER_FILTER.RELATED_JOBS) {
            ob$ = this.service().getRelatedSimulations(this.id());
        } else if (this.usersFilterFormControl.value === USER_FILTER.VERIFIED_JOBS) {
            ob$ = this.service().getVerifiedSimulations(this.id());
        } else {
            throw Error(`Not Implemented for ${this.usersFilterFormControl.value}`);
        }

        ob$.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                this.jobList.set(
                    orderBy(
                        response.result.map(sim => {
                            const job = JobModel.createFromAPI(sim);

                            return { ...job, actionList: this.getActionList(job) };
                        }),
                        'createdDate',
                        ['desc'],
                    ),
                );
            })
            .add(() => {
                this.isLoading.set(false);
                this.lastUpdatedTime.set(new Date());
            });
    }

    protected onJobClick(event: DxDataGrid.RowClickEvent<JobModel>): void {
        // FIXME: Using "CellCLickEvent" gives the cell clicked information. But,
        // The clicking on "Logs" link leads to url redirection to simulation
        // details page.
        //
        // So, Using "RowClickEvent" to avoid the link click redirection.
        // But, Problem here is, "Action" column click also triggers navigate
        // to simulation. To avoid that, adding this temp fix.
        //
        // If any better solution found, should be applied here.

        // NOTE: className can be SVGAnimatedString if the target is an SVG. So, we check that it is a string
        const className = event.event.target?.className;
        if (
            typeof className === 'string' &&
            (className?.includes('dx-icon dx-icon-overflow') ||
                className?.includes('dx-button-content'))
        ) {
            return;
        }

        this.navigateToJob(event.data);
    }

    protected navigateToJob(job: JobModel, showLogs?: boolean) {
        const jobUrl = RouterHelper.buildRoute({
            id: job.entityId,
            object: job.objectType,
            projectSlug: this.currentProject(),
            simulationId: job.id,
        });
        this.router.navigate([jobUrl], showLogs ? { queryParams: { showLogs } } : {});
    }

    protected onContextMenuPreparing(e: DxDataGrid.ContextMenuPreparingEvent): void {
        if (e.row?.rowType === 'data') {
            e.items = [
                ...(e.items || []),
                {
                    text: 'Open In New Tab',
                    onItemClick: () =>
                        window.open(
                            RouterHelper.buildRoute({
                                id: e.row.data.entityId,
                                object: e.row.data.objectType,
                                projectSlug: this.currentProject(),
                                simulationId: e.row.data.id,
                            }),
                            '_blank',
                        ),
                },
                {
                    text: 'Copy Link',
                    onItemClick: () =>
                        this.clipboardService.copyToClipboard(
                            window.location.origin +
                                RouterHelper.buildRoute({
                                    id: e.row.data.entityId,
                                    object: e.row.data.objectType,
                                    projectSlug: this.currentProject(),
                                    simulationId: e.row.data.id,
                                }),
                        ),
                },
            ];
        } else {
            e.items = [
                ...(e.items || []),
                {
                    text: 'Reset to original state',
                    onItemClick: () => this.dataGrid().instance.state(null),
                },
            ];
        }
    }

    private updateJobInfo() {
        // NOTE: We are adding this check here for better typesafety
        if (!(this.service() instanceof WithJobService)) throw Error(`Expected Job service`);

        this.editJob.isSaving.set(true);
        this.simulationService
            .updateSimulation(
                this.editJob.editJobForm.value.id,
                {
                    name: this.editJob.editJobForm.value.name,
                    description: this.editJob.editJobForm.value.description,
                    submissionDate: this.editJob.editJobForm.value.submissionDate
                        ? moment(this.editJob.editJobForm.value.submissionDate).format(
                              MOMENT_UTC_DATETIME_FORMAT,
                          )
                        : null,
                },
                false,
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    const updatedJob = JobModel.createFromAPI(response.result);
                    // Replace the item in the jobList
                    this.jobList.update(jobList =>
                        jobList.map(simulation =>
                            simulation.id === updatedJob.id
                                ? { ...updatedJob, actionList: this.getActionList(updatedJob) }
                                : simulation,
                        ),
                    );

                    this.toast.showSuccess(`${'Data is updated successfully'}`);
                },
                error: errorResponse => {
                    this.toast.showError(
                        `Error occurred while updating simulation data: ${this.apiErrorService
                            .getErrorMessages(errorResponse)
                            .join(', ')}`,
                    );
                },
            })
            .add(() => {
                this.editJob.isEditModalOpen.set(false);
                this.editJob.isSaving.set(false);
            });
    }

    private onEditJob(data: JobModel) {
        this.editJob.isEditModalOpen.set(true);
        this.editJob.job.set(data);
        this.editJob.editJobForm = new FormGroup({
            id: new FormControl(data.id, { validators: [Validators.required] }),
            name: new FormControl(data.descriptionSectionData.name, {
                validators: [Validators.required],
            }),
            description: new FormControl(data.descriptionSectionData.description),
            submissionDate: new FormControl(data.descriptionSectionData.submissionDate),
        });
    }

    protected onEditJobSubmit() {
        if (this.editJob.editJobForm.invalid) {
            FormHelper.validateAllFormFields(this.editJob.editJobForm);
            this.toast.showError('This form has error(s), saving failed');
            console.log('Form Errors', FormHelper.getAllErrors(this.editJob.editJobForm));

            return;
        }
        this.updateJobInfo();
    }

    closeEditJob() {
        this.editJob.isEditModalOpen.set(false);
        this.editJob.job.set(null);
        this.editJob.editJobForm.reset();
        this.editJob.isSaving.set(false);
    }

    private onDeleteJob(data: JobModel) {
        this.confirmationService.confirm({
            key: 'confirm-dialog',
            message: `Do you want to delete selected ${data.executionType}?`,
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            closeOnEscape: true,
            acceptLabel: 'Delete',
            rejectLabel: 'Cancel',
            rejectButtonProps: {
                severity: 'secondary',
                outlined: true,
                size: 'small',
                styleClass: 'rounded',
            },
            acceptButtonProps: {
                severity: 'danger',
                size: 'small',
                styleClass: 'rounded',
            },
            accept: () => {
                this.simulationService
                    .deleteSimulation(data.id)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.toast.showSuccess(`${data.executionType} deleted successfully`);

                        this.jobList.update(jobList => jobList.filter(s => s.id !== data.id));
                    });
            },
        });
    }

    onReRun(data: JobModel) {
        const queryParams: RunJobQueryParams = {
            type: data.executionType,
            entityId: data.inputsSectionData.currentObjectData.entityId,
            jobId: data.id,
        };

        const url = RouterHelper.buildRoute({
            object: this.object(),
            id: this.id(),
            projectSlug: this.currentProject(),
        });
        this.router.navigate([`${url}/run-job`], { queryParams });
    }

    private onReRunReports(data: JobModel) {
        const queryParams: RunJobQueryParams = {
            type: data.executionType,
            entityId: data.inputsSectionData.currentObjectData.entityId,
            jobId: data.id,
            rerunReports: true,
        };

        const url = RouterHelper.buildRoute({
            object: this.object(),
            id: this.id(),
            projectSlug: this.currentProject(),
        });
        this.router.navigate([`${url}/run-job`], { queryParams });
    }

    /** When making changes to this URL - also need to change job-dashboard > hillClimbingLink() */
    private onHillClimbOnPrompt(data: JobModel) {
        const queryParams: RunJobQueryParams = {
            type: EXECUTION_TYPE.HILL_CLIMBING,
            entityId: data.inputsSectionData.currentObjectData.entityId,
            jobId: data.id, // Fetch evaluation logic and reports used from here
            baseHillclimbSimId: data.id,
        };

        const url = RouterHelper.buildRoute({
            object: this.object(),
            id: this.id(),
            projectSlug: this.currentProject(),
        });
        this.router.navigate([`${url}/run-job`], { queryParams });
    }

    private pinDashboard(job: ArrayItem<SignalValue<JobHistoryComponent['jobList']>>): void {
        this.simulationService
            .pinSimulation(job.id, { isPinned: !job.isPinned })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: response => {
                    const updatedJob = JobModel.createFromAPI(response.result);
                    this.jobList.update(jobList =>
                        jobList.map(simulation =>
                            simulation.id === updatedJob.id
                                ? { ...updatedJob, actionList: this.getActionList(updatedJob) }
                                : simulation,
                        ),
                    );
                    if (updatedJob.isPinned) {
                        this.toast.showSuccess('Dashboard pinned successfully');
                    } else {
                        this.toast.showSuccess('Dashboard unpinned successfully');
                    }
                    this.getPinnedSimulations();
                },
            });
    }

    ngOnDestroy() {
        this.jobSubscriber?.unsubscribe();
    }
}
